import React from 'react';
import { Heart, Users, Award } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const About = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-20 bg-nanny-cream">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-nanny-dark mb-6 heading-font">Our Story</h1>
            <p className="text-xl text-gray-700">From Our Kitchen To Yours Since 2021</p>
          </div>
        </div>
      </section>

      {/* Meet the Owners */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1">
                <h2 className="text-4xl md:text-5xl font-bold text-nanny-dark mb-6 heading-font">Meet Greg & Jennifer Webb</h2>
                <div className="space-y-4 text-gray-700 text-lg">
                  <p>
                    Welcome to Nanny's! We're Greg and Jennifer Webb, the proud owners of this homemade meal and catering business that we started in 2021.
                  </p>
                  <p>
                    Our journey began with a simple passion: creating delicious, homemade food that brings people together. Named "Nanny's" to honor the tradition of home cooking passed down through generations, we pour our hearts into every dish we prepare.
                  </p>
                  <p>
                    From our humble beginnings in Huntingdon, TN, we've grown into a beloved part of the community, serving take-home meals Monday through Thursday and providing catering services seven days a week.
                  </p>
                  <p>
                    What started as a dream to share our love of cooking has blossomed into a business that feeds families, celebrates milestones, and creates memories around the dinner table.
                  </p>
                </div>
              </div>
              <div className="order-1 lg:order-2">
                <div className="relative">
                  <div className="absolute -inset-4 bg-nanny-maroon/10 rounded-2xl"></div>
                  <img 
                    src="https://customer-assets.emergentagent.com/job_d5dd8d35-1c0f-4833-a146-f7afb485112b/artifacts/w5hfr2xa_462848259_8521030924610059_540397433922456773_n.jpg" 
                    alt="Greg and Jennifer Webb" 
                    className="relative rounded-xl shadow-2xl w-full"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-nanny-cream">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-nanny-dark mb-4 heading-font">What We Stand For</h2>
              <p className="text-xl text-gray-600">The values that guide everything we do</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <Card className="border-2 border-transparent hover:border-nanny-maroon transition-all duration-300">
                <CardContent className="p-8 text-center">
                  <div className="bg-white rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6 shadow-lg">
                    <Heart className="h-10 w-10 text-nanny-maroon" />
                  </div>
                  <h3 className="text-2xl font-bold text-nanny-dark mb-4">Made with Love</h3>
                  <p className="text-gray-600">
                    Every dish is prepared with care and attention to detail, just like cooking for our own family.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-transparent hover:border-nanny-maroon transition-all duration-300">
                <CardContent className="p-8 text-center">
                  <div className="bg-white rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6 shadow-lg">
                    <Users className="h-10 w-10 text-nanny-maroon" />
                  </div>
                  <h3 className="text-2xl font-bold text-nanny-dark mb-4">Community First</h3>
                  <p className="text-gray-600">
                    We're proud to serve Huntingdon and surrounding areas, building relationships one meal at a time.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-transparent hover:border-nanny-maroon transition-all duration-300">
                <CardContent className="p-8 text-center">
                  <div className="bg-white rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6 shadow-lg">
                    <Award className="h-10 w-10 text-nanny-maroon" />
                  </div>
                  <h3 className="text-2xl font-bold text-nanny-dark mb-4">Quality Always</h3>
                  <p className="text-gray-600">
                    Fresh ingredients, homemade recipes, and no shortcuts - that's our promise to you.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Our Kitchen Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-nanny-dark mb-8 heading-font">From Our Kitchen To Yours</h2>
            <p className="text-xl text-gray-700 mb-8">
              Every morning, we start fresh - preparing meals that we'd be proud to serve our own family. Whether it's our weekly take-home menu that changes to keep things exciting, or catering for your special event, we treat every order with the same care and dedication.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-12">
              <div className="text-center">
                <div className="text-4xl font-bold text-nanny-maroon mb-2">2021</div>
                <div className="text-gray-600">Established</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-nanny-maroon mb-2">1000+</div>
                <div className="text-gray-600">Happy Customers</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-nanny-maroon mb-2">7</div>
                <div className="text-gray-600">Days Catering</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-nanny-maroon mb-2">100%</div>
                <div className="text-gray-600">Homemade</div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
